import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { GtmEventService } from '@core/analytics/gtm-events.service';
import {
  CaseData,
  ProgressData,
  SaleData,
  totalCases,
} from '@core/interfaces/partners-ALI';
import { Carousel } from 'primeng/carousel';
import { AlliesProgressModalComponent } from '../allies-progress-modal/allies-progress-modal.component';
import { UtilsService } from '@core/services/utils/utils.service';
@Component({
  selector: 'app-allies-progress',
  templateUrl: './allies-progress.component.html',
  styleUrls: ['./allies-progress.component.scss'],
})
export class AlliesProgressComponent implements OnInit {
  @Input() currentYear;
  @Input() mockProgressData!: ProgressData;
  @Input() saleData: SaleData | null = null;

  @Output() scrollToBenefits = new EventEmitter<void>();

  @ViewChild('carouselGeneralALI') carouselGeneralALI!: Carousel;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  alignmentClass = 'text-start-dynamic';
  activeCaseData: CaseData | null = null;

  // Niveles de metas
  silver;
  gold;
  diamond;

  // Flags para animaciones en el carousel
  hasEntered = false;
  hasEntered2 = false;

  // Acceso a funciones matemáticas en el template
  readonly Math = Math;

  // Mock de cards para mostrar en carousel
  readonly cards = [
    { id: 1, name: 'Card 1' },
    { id: 2, name: 'Card 2' },
    { id: 3, name: 'Card 3' },
  ];

  constructor(
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly _gtmEventService: GtmEventService
  ) { }

  ngOnInit() {
    this.setActiveCaseData();
    this.setLevelTargets();
    this.updateProgressDataFromService();
    this.updateTextAlignment();
  }

  private setActiveCaseData() {
    const code = this.saleData?.actualState?.case;
    const incomingTitle = this.saleData?.actualState?.description;

    const matchedCase = totalCases.find((c) => c.case === code);

    if (matchedCase) {
      this.activeCaseData = {
        ...matchedCase,
        title: incomingTitle || matchedCase.title,
      };
    }
  }

  private setLevelTargets() {
    if (
      !this.saleData?.customerSaleLevels ||
      this.saleData.customerSaleLevels.length < 4
    )
      return;

    this.silver = this.saleData.customerSaleLevels[1]?.targetAmount;
    this.gold = this.saleData.customerSaleLevels[2]?.targetAmount;
    this.diamond = this.saleData.customerSaleLevels[3]?.targetAmount;
  }

  updateProgressDataFromService() {
    if (!this.saleData) return;

    const currentAmount = this.saleData.invocedTotalAmount || 0;
    const progressPercentage =
      this.saleData.customerActualLevel?.progressPercentage || 0;

    this.mockProgressData.currentAmount = currentAmount;
    this.mockProgressData.progressPercentage = progressPercentage;
    this.mockProgressData.animatedProgress = 0;
    this.mockProgressData.avatarPosition = progressPercentage;

    this.mockProgressData.remainingForSilver =
      this.getRemainingForLevelFromService('Plata');
    this.mockProgressData.remainingForGold =
      this.getRemainingForLevelFromService('Oro');
    this.mockProgressData.silverProgress =
      this.getProgressToLevelFromService('Diamante');
    this.mockProgressData.monthlyAverage =
      this.saleData.customerActualLevel?.monthlyAverageToNext || 0;

    this.calculateProgress();
  }

 // helper: targets por posición
private getTargets() {
  const levels = this.saleData?.customerSaleLevels ?? [];
  const byPos = (pos: number) => levels.find(l => l.position === pos)?.targetAmount ?? 0;
  return {
    bronze:  byPos(1),
    silver:  byPos(2),
    gold:    byPos(3),
    diamond: byPos(4),
  };
}

// helper: comparación con tolerancia
private nearlyEqual(a: number, b: number, eps = 0.2): boolean {
  return Math.abs(a - b) <= eps;
}

// Reemplazo de calculateProgress()
calculateProgress() {
  const amount = this.saleData?.invocedTotalAmount ?? 0;
  if (amount == null || !this.saleData?.customerSaleLevels?.length) return;

  // Valores visuales/hitos del diseño (ajusta si cambia el UI)
  const VISUAL = [0, 35.5, 64.6, 100];

  const { bronze, silver, gold, diamond } = this.getTargets();
  const targets = [bronze, silver, gold, diamond];

  // Si no hay targets válidos
  const lastTarget = targets[targets.length - 1] || 1;
  if (lastTarget <= 0) {
    this.mockProgressData.avatarPosition = 0;
    this.mockProgressData.progressPercentage = 0;
    return;
  }

  // Si supera el último target → 100%
  if (amount >= lastTarget) {
    const pos = 100;
    this.mockProgressData.avatarPosition = pos;
    this.mockProgressData.progressPercentage = pos;
    this.animateProgressBar();
    return;
  }

  // Buscar el tramo donde cae "amount"
  let visualPos = 0;
  for (let i = 0; i < targets.length - 1; i++) {
    const from = targets[i];
    const to = targets[i + 1];

    // Evitar divisiones por 0
    if (to <= from) {
      if (amount <= to) {
        visualPos = VISUAL[i + 1];
        break;
      } else {
        continue;
      }
    }

    // Si amount está en este tramo [from, to]
    if (amount <= to) {
      const relative = Math.min(Math.max((amount - from) / (to - from), 0), 1);
      const startV = VISUAL[i];
      const endV = VISUAL[i + 1];
      visualPos = startV + relative * (endV - startV);
      break;
    }
  }

  // Snap: si estamos muy cerca de un hito visual, alineamos exacto
  const isNearSnap = VISUAL.some(p => this.nearlyEqual(p, visualPos, 0.2));
  this.mockProgressData.avatarPosition = Math.min(Math.max(0, visualPos), 100);
  this.mockProgressData.progressPercentage = this.mockProgressData.avatarPosition;

  // animar la barra con el nuevo porcentaje visual
  this.animateProgressBar();
}

  // Animar la barra de progreso desde 0 hasta el valor actual
  animateProgressBar() {
    const targetProgress = this.mockProgressData.progressPercentage;
    const duration = 1500; // 1.5 segundos
    const steps = 60; // 60 pasos para 60fps
    const increment = targetProgress / steps;
    const stepDuration = duration / steps;

    this.mockProgressData.animatedProgress = 0;

    const timer = setInterval(() => {
      this.mockProgressData.animatedProgress += increment;

      if (this.mockProgressData.animatedProgress >= targetProgress) {
        this.mockProgressData.animatedProgress = targetProgress;
        clearInterval(timer);
      }

      // Forzar la detección de cambios
      this.cdr.detectChanges();
    }, stepDuration);
  }

  getRemainingForLevelFromService(levelName: string): number {
    if (!this.saleData) return 0;

    const level = this.saleData.customerSaleLevels.find(
      (l) => l.name.toLowerCase() === levelName.toLowerCase()
    );

    if (!level) return 0;

    const remaining = level.targetAmount - this.saleData.invocedTotalAmount;

    return Math.max(remaining, 0); // Nunca devolver negativo
  }

  getProgressToLevelFromService(levelName: string): number {
    if (!this.saleData) return 0;

    const level = this.saleData.customerSaleLevels.find(
      (l) => l.name.toLowerCase() === levelName.toLowerCase()
    );

    if (!level) return 0;

    const current = this.saleData.invocedTotalAmount;
    const progress = (current / level.targetAmount) * 100;

    return Math.min(Math.max(progress, 0), 100); // Limita entre 0 y 100
  }

  shouldShowTooltip(level: string): boolean {
    const currentPosition = this.saleData?.customerActualLevel?.position;

    if (currentPosition === undefined) {
      return false;
    }

    const levelPositions: { [key: string]: number } = {
      bronce: 1,
      silver: 2,
      gold: 3,
      diamond: 4,
    };

    const levelToCheck = levelPositions[level.toLowerCase()];
    return levelToCheck > currentPosition;
  }

  getAvatarPosition(): number {
    const raw = this.mockProgressData?.avatarPosition ?? 0;

    const SNAP_POINTS = [0, 33.3, 66.6];
    const SNAP_TOLERANCE = 0.2;
    const MAX_POSITION = 97;
    const ADJUST_OFFSET = 1.8;

    const isExactStep = SNAP_POINTS.some(
      (point) => Math.abs(point - raw) < SNAP_TOLERANCE
    );

    if (isExactStep) {
      return raw;
    }

    // Aseguramos que no supere el máximo permitido
    return Math.min(Math.max(0, raw - ADJUST_OFFSET), MAX_POSITION);
  }

  // Obtener el promedio mensual requerido hasta fin de año
  getMonthlyAverageRequired(): number {
    const current = this.mockProgressData.currentAmount;
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-11 (Enero = 0)
    const monthsRemaining = 12 - currentMonth; // Meses restantes del año

    let amountNeeded = 0;

    if (current < this.silver) {
      // Bronce -> Plata
      amountNeeded = this.silver - current;
    } else if (current < this.gold) {
      // Plata -> Oro
      amountNeeded = this.gold - current;
    } else if (current < this.diamond) {
      // Oro -> Diamante
      amountNeeded = this.diamond - current;
    } else {
      // Diamante: si supera el objetivo, mostrar negativo
      if (current > this.diamond) {
        return -(current - this.diamond);
      }
      return 0; // Solo si está exactamente en 350k
    }

    // Calcular promedio mensual
    const monthlyAverage = amountNeeded / monthsRemaining;

    // El promedio nunca puede ser negativo (excepto para Diamante que ya superó el nivel)
    return Math.max(monthlyAverage, 0);
  }

  getLocalProgress(slideId: number, isForAvatar: boolean): number {
    const amount = this.saleData?.invocedTotalAmount ?? 0;
    const levels = this.saleData?.customerSaleLevels ?? [];

    if (!levels.length) return 0;

    const isMobile = window.innerWidth <= 380 ? 85 : 90.5;
    const maxProgress = isForAvatar ? isMobile : 100;

    // Definir rangos según slide
    const slideToLevels: Record<number, [number, number]> = {
      1: [1, 2], // Bronce → Plata
      2: [2, 3], // Plata → Oro
      3: [3, 4], // Oro → Diamante
    };

    const [fromPos, toPos] = slideToLevels[slideId] || [];
    const fromLevel = levels.find(l => l.position === fromPos);
    const toLevel = levels.find(l => l.position === toPos);

    if (!fromLevel || !toLevel) return 0;

    const fromTarget = fromLevel.targetAmount ?? 0;
    const toTarget = toLevel.targetAmount ?? 0;

    // Caso 1: ya superó el rango → progreso completo
    if (amount >= toTarget) {
      return maxProgress;
    }

    // Caso 2: aún no llegó → progreso relativo
    if (amount > fromTarget) {
      const relative = ((amount - fromTarget) / (toTarget - fromTarget)) * maxProgress;
      return Math.min(Math.max(relative, 0), maxProgress);
    }

    // Caso 3: no alcanzó ni el inicio del tramo
    return 0;
  }

  // Obtener la medalla del próximo nivel
  achievementCardMiddle(actualState: number): string {
    switch (actualState) {
      case 1:
        return 'achievement-card-large-1';
      case 2:
        return 'achievement-card-large-2';
      case 3:
        return 'achievement-card-large-3';
      case 4:
        return 'achievement-card-large-4';
      case 5:
        return 'achievement-card-large-5';
      case 6:
        return 'achievement-card-large-6';
      default:
        return ''; // Valor por defecto
    }
  }

  // Obtener la medalla del próximo nivel
  getNextLevelMedal(current: string): string {
    switch (current) {
      case 'Bronce':
        return '/assets/image/partners-ali/medal-bronce.svg';
      case 'Plata':
        return '/assets/image/partners-ali/medal-plata.svg';
      case 'Oro':
        return '/assets/image/partners-ali/medal-oro.svg';
      case 'Diamante':
        return '/assets/image/partners-ali/medal-diamond.svg';
      default:
        return '/assets/image/partners-ali/medal-bronce.svg'; // Valor por defecto
    }
  }

  // Obtener el faltante para un nivel específico
  getRemainingForLevel(level: string): number {
    const current = this.mockProgressData.currentAmount;

    switch (level) {
      case 'silver':
        return Math.max(this.silver - current, 0);
      case 'gold':
        return Math.max(this.gold - current, 0);
      case 'diamond':
        return Math.max(this.diamond - current, 0);
      default:
        return 0;
    }
  }
  shouldShowAvatar(cardId: number): boolean {
    return this.getCurrentLevelCards().includes(cardId);
  }
  getCurrentLevelCards(): number[] {
    const amount = this.saleData?.invocedTotalAmount ?? 0;
    const levels = this.saleData?.customerSaleLevels ?? [];

    if (!levels.length) return [];

    const cards: number[] = [];

    // Definir los tramos según card
    const slideToLevels: Record<number, [number, number]> = {
      1: [1, 2], // Bronce → Plata
      2: [2, 3], // Plata → Oro
      3: [3, 4], // Oro → Diamante
    };

    for (const [cardId, [fromPos, toPos]] of Object.entries(slideToLevels)) {
      const fromLevel = levels.find(l => l.position === fromPos);
      const toLevel = levels.find(l => l.position === toPos);

      if (!fromLevel || !toLevel) continue;

      const fromTarget = fromLevel.targetAmount ?? 0;
      const toTarget = toLevel.targetAmount ?? 0;

      // Caso especial: si está exactamente en el límite, mostrar en ambos
      if (amount === toTarget) {
        cards.push(Number(cardId), Number(cardId) + 1);
        break;
      }

      // Caso normal: si está dentro del tramo → asignar ese card
      if (amount >= fromTarget && amount < toTarget) {
        cards.push(Number(cardId));
        break;
      }

      // Si superó todos → última card
      if (amount >= levels[levels.length - 1].targetAmount) {
        cards.push(3);
        break;
      }
    }

    return cards;
  }

  getLevelMessageStrict(levelName: string): string | null {
    const currentLevel = this.saleData?.actualLevelName;
    const currentCase = this.saleData?.actualState?.case;

    if (currentLevel !== levelName || currentCase == null) return null;

    if ([1, 2].includes(currentCase)) {
      return 'Asegura tu nivel';
    }

    if ([3, 4].includes(currentCase) && levelName !== 'Diamante') {
      return 'Aseguraste tu nivel';
    }

    return null;
  }

  @HostListener('window:resize', [])
  onWindowResize() {
    this.updateTextAlignment();
  }

  updateTextAlignment() {
    const threshold = 7;
    const avatarPosition = this.mockProgressData.avatarPosition;
    if (avatarPosition <= threshold) {
      this.alignmentClass = 'text-start-dynamic';
    } else if (avatarPosition >= 80) {
      this.alignmentClass = 'text-end-dynamic';
    } else {
      this.alignmentClass = 'text-center-dynamic';
    }
    this.cdr.detectChanges();
  }

  alignmentClassmobile(idCard) {
    const threshold = 15;
    const avatarPosition = this.getLocalProgress(idCard, true);


    if (avatarPosition <= threshold) {
      return 'text-start-dynamic';
    } else if (avatarPosition >= 72) {
      return 'text-end-dynamic';
    } else {
      return 'text-center-dynamic';
    }
  }

  getTooltipPositionClass(level: string): string {
    switch (level.toLowerCase()) {
      case 'bronze':
        return 'tooltip-left';
      case 'silver':
        return 'tooltip-center';
      case 'gold':
        return 'tooltip-center';
      case 'diamond':
        return 'tooltip-right';
      default:
        return 'tooltip-center';
    }
  }
  prevPage() {
    this.carouselGeneralALI.navBackward(new Event('click'));
  }

  nextPage() {
    if (this.carouselGeneralALI?.page == 1) {
      this.hasEntered2 = true;
    } else {
      this.hasEntered = true;
    }
    this.carouselGeneralALI.navForward(new Event('click'));
    setTimeout(() => {
      this.hasEntered = false;
      this.hasEntered2 = false;
    }, 2000); // 2000 ms = 2segundos
  }

  goToPage(page: number) {
    this.carouselGeneralALI.page = page;
    if (this.carouselGeneralALI?.page == 1) {
      this.hasEntered2 = true;
    } else {
      this.hasEntered = true;
    }
    setTimeout(() => {
      if (this.carouselGeneralALI?.page == 1) {
        this.hasEntered2 = false;
      } else {
        this.hasEntered = false;
      }
    }, 2000); // 2000 ms = 2segundos
  }

  getTotalPages() {
    return this.cards;
  }
  // Método para obtener índice del card
  getCardIndex(card: any): number {
    return this.cards.indexOf(card);
  }
  // Mostrar más información del progreso
  showMoreInfo() {
    this._gtmEventService.clickElementEvent({
      cta: 'Más información',
      seccion: 'Aliados beneficios',
      subSeccion: 'Tu progreso en el programa',
    });
    this.moreInfoOpenModal();
  }

  moreInfoOpenModal() {
    const isMobile = window.innerWidth <= 767;

    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = isMobile
      ? 'allies-progress-modal-mobile'
      : 'allies-progress-modal';

    this.dialog.open(AlliesProgressModalComponent, {
      width: '70rem',
      height: isMobile ? '96vh' : '85vh',
      maxWidth: isMobile ? '96vw' : '95vw',
      panelClass: 'custom-dialog-padding',
      data: {
        currentYear: this.currentYear,
        mockProgressData: this.mockProgressData,
        alignmentClass: this.alignmentClass,
        saleData: this.saleData,
        activeCaseData: this.activeCaseData,
      },
    });
  }
  formatTotalNumber(value) {
    return UtilsService.formatTotalNumber(value);
  }
  onScrollToBenefits(event: Event) {
    event.preventDefault(); // evitar el scroll automático del href="#"
    this.scrollToBenefits.emit();
  }
}
